import type { LucideIcon } from 'lucide-react'

import {
    AppWindow,
    ArrowDown,
    ArrowLeft,
    ArrowRight,
    ArrowUp,
    FastForward,
    LayoutGrid,
    Mic,
    Moon,
    NotebookPen,
    Pipette,
    Play,
    Rewind,
    ScanEye,
    Search,
    Sun,
    SunDim,
    Trash2,
    Volume1,
    Volume2,
    VolumeX
} from 'lucide-react'

export type KeyVariant = 'alpha' | 'pair' | 'mod' | 'fn' | 'icon' | 'hyper' | 'grille' | 'knob' | 'blank'

export interface KeyDef {
    id: string
    variant: KeyVariant
    w?: number
    label?: string
    top?: string
    icon?: LucideIcon
    textured?: boolean
    italic?: boolean
    bump?: boolean
    codes?: string[]
}

export interface KeyPlacement {
    id: string
    row: number
    x: number
    w: number
    hue: number
}

const letter = (char: string, bump = false): KeyDef => ({
    id: `key-${char.toLowerCase()}`,
    variant: 'alpha',
    label: char,
    bump,
    codes: [`Key${char}`]
})

const pair = (id: string, top: string, label: string, code: string): KeyDef => ({
    id,
    variant: 'pair',
    top,
    label,
    codes: [code]
})

const fn = (index: number, icon: LucideIcon): KeyDef => ({
    id: `f${index}`,
    variant: 'fn',
    label: `F${index}`,
    icon,
    codes: [`F${index}`]
})

export const KEYBOARD_ROWS: KeyDef[][] = [
    [
        { id: 'esc', variant: 'grille', codes: ['Escape'] },
        fn(1, SunDim),
        fn(2, Sun),
        fn(3, LayoutGrid),
        fn(4, Search),
        fn(5, Mic),
        fn(6, Moon),
        fn(7, Rewind),
        fn(8, Play),
        fn(9, FastForward),
        fn(10, VolumeX),
        fn(11, Volume1),
        fn(12, Volume2),
        { id: 'screenshot', variant: 'icon', icon: ScanEye, codes: ['PrintScreen'] },
        { id: 'trash', variant: 'icon', icon: Trash2, codes: ['Delete'] },
        { id: 'knob', variant: 'knob' }
    ],
    [
        pair('backquote', '~', '`', 'Backquote'),
        pair('digit-1', '!', '1', 'Digit1'),
        pair('digit-2', '@', '2', 'Digit2'),
        pair('digit-3', '#', '3', 'Digit3'),
        pair('digit-4', '$', '4', 'Digit4'),
        pair('digit-5', '%', '5', 'Digit5'),
        pair('digit-6', '^', '6', 'Digit6'),
        pair('digit-7', '&', '7', 'Digit7'),
        pair('digit-8', '*', '8', 'Digit8'),
        pair('digit-9', '(', '9', 'Digit9'),
        pair('digit-0', ')', '0', 'Digit0'),
        pair('minus', '_', '-', 'Minus'),
        pair('equal', '+', '=', 'Equal'),
        { id: 'backspace', variant: 'mod', w: 2, label: 'Backspace', codes: ['Backspace'] },
        { id: 'color-picker', variant: 'icon', icon: Pipette, codes: ['Home'] }
    ],
    [
        { id: 'tab', variant: 'mod', w: 1.5, label: 'Tab', italic: true, codes: ['Tab'] },
        letter('Q'),
        letter('W'),
        letter('E'),
        letter('R'),
        letter('T'),
        letter('Y'),
        letter('U'),
        letter('I'),
        letter('O'),
        letter('P'),
        pair('bracket-left', '{', '[', 'BracketLeft'),
        pair('bracket-right', '}', ']', 'BracketRight'),
        { id: 'backslash', variant: 'pair', w: 1.5, top: '|', label: '\\', codes: ['Backslash'] },
        { id: 'notes', variant: 'icon', icon: NotebookPen, codes: ['PageUp'] }
    ],
    [
        { id: 'hyper', variant: 'hyper', w: 1.75, codes: ['CapsLock'] },
        letter('A'),
        letter('S'),
        letter('D'),
        letter('F', true),
        letter('G'),
        letter('H'),
        letter('J', true),
        letter('K'),
        letter('L'),
        pair('semicolon', ':', ';', 'Semicolon'),
        pair('quote', '"', "'", 'Quote'),
        { id: 'enter', variant: 'mod', w: 2.25, label: 'Raycast', codes: ['Enter', 'NumpadEnter'] },
        { id: 'windows', variant: 'icon', icon: AppWindow, codes: ['PageDown'] }
    ],
    [
        { id: 'shift-left', variant: 'mod', w: 2.25, label: 'Shift', textured: true, italic: true, codes: ['ShiftLeft'] },
        letter('Z'),
        letter('X'),
        letter('C'),
        letter('V'),
        letter('B'),
        letter('N'),
        letter('M'),
        pair('comma', '<', ',', 'Comma'),
        pair('period', '>', '.', 'Period'),
        pair('slash', '?', '/', 'Slash'),
        { id: 'shift-right', variant: 'mod', w: 1.75, label: 'Shift', textured: true, italic: true, codes: ['ShiftRight'] },
        { id: 'arrow-up', variant: 'icon', icon: ArrowUp, codes: ['ArrowUp'] },
        { id: 'dictation', variant: 'icon', icon: Mic, codes: ['End'] }
    ],
    [
        { id: 'ctrl-left', variant: 'mod', w: 1.25, label: 'Ctrl', textured: true, codes: ['ControlLeft'] },
        { id: 'opt-left', variant: 'mod', w: 1.25, label: 'Opt', textured: true, codes: ['AltLeft'] },
        { id: 'cmd-left', variant: 'mod', w: 1.25, label: 'Cmd', textured: true, codes: ['MetaLeft'] },
        { id: 'space', variant: 'blank', w: 6.25, codes: ['Space'] },
        { id: 'cmd-right', variant: 'mod', label: 'Cmd', codes: ['MetaRight'] },
        { id: 'fn', variant: 'mod', label: 'Fn', codes: ['AltRight'] },
        { id: 'ctrl-right', variant: 'mod', label: 'Ctrl', codes: ['ControlRight'] },
        { id: 'arrow-left', variant: 'icon', icon: ArrowLeft, codes: ['ArrowLeft'] },
        { id: 'arrow-down', variant: 'icon', icon: ArrowDown, codes: ['ArrowDown'] },
        { id: 'arrow-right', variant: 'icon', icon: ArrowRight, codes: ['ArrowRight'] }
    ]
]

export const KEY_BY_CODE: ReadonlyMap<string, string> = new Map(
    KEYBOARD_ROWS.flat().flatMap(key => (key.codes ?? []).map(code => [code, key.id] as [string, string]))
)

/*
 * Where every key sits, in key units, plus the backlight hue it glows with.
 * The hue sweeps from yellow green on the left to violet on the right.
 */
export const KEY_PLACEMENTS: KeyPlacement[] = KEYBOARD_ROWS.flatMap((row, rowIndex) => {
    let x = 0

    return row.map(key => {
        const w = key.w ?? 1
        const placement = { id: key.id, row: rowIndex, x, w, hue: Math.round(58 + 16 * (x + w / 2)) }

        x += w

        return placement
    })
})

export const KEY_HUE: ReadonlyMap<string, number> = new Map(KEY_PLACEMENTS.map(placement => [placement.id, placement.hue]))

export const ROW_SHADE = [0, 0.06, 0.16, 0.3, 0.46, 0.62]

export const LED_COLORS = [
    '#ffe4de',
    '#ffc9bf',
    '#ffe4de',
    '#ffc9bf',
    '#ffe4de',
    '#ffc9bf',
    '#ffe4de',
    '#ffc9bf',
    '#ffe4de',
    '#ffc9bf'
]
